import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { COLORS, TYPOGRAPHY } from '../../lib/styleGuide';
import { ShieldDone, TickSquare, InfoCircle, TimeCircle } from 'react-iconly';
import { useOnboardingStatus } from '../../hooks/useOnboardingStatus';
import { getEnvironmentConfig } from '../../lib/environment';

const ACCENT_GRAY = '#757575';
const SUCCESS_GREEN = '#10B981';
const WARNING_ORANGE = '#F59E0B';
const ERROR_RED = '#F44336';

export default function VerifyIdentityStep({ onNext }) {
  const { currentUser } = useAuth();
  const [verificationStatus, setVerificationStatus] = useState('not_started');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [verificationUrl, setVerificationUrl] = useState('');
  const { startIdentityVerification, completeIdentityVerification } = useOnboardingStatus();
  const { apiBaseUrl } = getEnvironmentConfig();

  // Check verification status on component mount
  useEffect(() => {
    if (currentUser?.uid) {
      checkVerificationStatus();
    }
  }, [currentUser]);

  // Auto-redirect verified users to seller hub
  useEffect(() => {
    if (verificationStatus === 'verified') {
      // Mark identity verification as completed in our system
      completeIdentityVerification('verified');
    }
  }, [verificationStatus, completeIdentityVerification]);

  const checkVerificationStatus = async () => {
    try {
      const response = await fetch(`${apiBaseUrl}/getIdentityStatus?userId=${currentUser.uid}`);
      if (response.ok) {
        const data = await response.json();
        setVerificationStatus(data.status);
      } else {
        console.error('Failed to fetch verification status:', response.status);
        // Fallback: set status to not_started if API is not available
        setVerificationStatus('not_started');
      }
    } catch (error) {
      console.error('Error checking verification status:', error);
      // Fallback: set status to not_started if API is not available
      setVerificationStatus('not_started');
    }
  };

  const startVerification = async () => {
    if (!currentUser?.uid) {
      setError('User not authenticated');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      // Mark identity verification as started
      await startIdentityVerification();
      
      const response = await fetch(`${apiBaseUrl}/createIdentitySession`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId: currentUser.uid,
          userEmail: currentUser.email,
          userName: currentUser.displayName || 'Unknown'
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setVerificationUrl(data.verificationUrl);
        setVerificationStatus('pending');
        
        // Redirect to Stripe Identity verification
        window.location.href = data.verificationUrl;
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create verification session');
      }
    } catch (error) {
      console.error('Identity verification API error:', error);
      setError('Identity verification service is temporarily unavailable. Please try again later or contact support.');
    } finally {
      setIsLoading(false);
    }
  };

  const renderStatusContent = () => {
    
    switch (verificationStatus) {
      case 'verified':
        return (
          <div style={{ textAlign: 'left', padding: '32px 0' }}>
            <h3 style={{ 
              fontSize: TYPOGRAPHY.ONBOARDING.STEP_HEADING, 
              color: SUCCESS_GREEN, 
              margin: '0 0 8px 0',
              fontWeight: 600
            }}>
              Verification Complete!
            </h3>
            <p style={{ 
              fontSize: TYPOGRAPHY.ONBOARDING.STEP_DESCRIPTION, 
              color: ACCENT_GRAY, 
              margin: '0 0 24px 0',
              lineHeight: 1.5
            }}>
              Your identity has been successfully verified. You're now ready to start selling on Vesta!
            </p>
          </div>
        );

      case 'pending':
        return (
          <div style={{ textAlign: 'left', padding: '32px 0' }}>
            <h3 style={{ 
              fontSize: TYPOGRAPHY.ONBOARDING.STEP_HEADING, 
              color: WARNING_ORANGE, 
              margin: '0 0 8px 0',
              fontWeight: 600
            }}>
              Verification in Progress
            </h3>
            <p style={{ 
              fontSize: TYPOGRAPHY.ONBOARDING.STEP_DESCRIPTION, 
              color: ACCENT_GRAY, 
              margin: '0 0 24px 0',
              lineHeight: 1.5
            }}>
              Your verification is being processed. This usually takes a few minutes.
            </p>
          </div>
        );

      case 'requires_input':
        return (
          <div style={{ textAlign: 'left', padding: '32px 0' }}>
            <h3 style={{ 
              fontSize: TYPOGRAPHY.ONBOARDING.STEP_HEADING, 
              color: WARNING_ORANGE, 
              margin: '0 0 8px 0',
              fontWeight: 600
            }}>
              Additional Information Required
            </h3>
            <p style={{ 
              fontSize: TYPOGRAPHY.ONBOARDING.STEP_DESCRIPTION, 
              color: ACCENT_GRAY, 
              margin: '0 0 24px 0',
              lineHeight: 1.5
            }}>
              We need more information to complete your verification. Please try again.
            </p>
          </div>
        );

      case 'failed':
        return (
          <div style={{ textAlign: 'left', padding: '32px 0' }}>
            <h3 style={{ 
              fontSize: TYPOGRAPHY.ONBOARDING.STEP_HEADING, 
              color: ERROR_RED, 
              margin: '0 0 8px 0',
              fontWeight: 600
            }}>
              Verification Failed
            </h3>
            <p style={{ 
              fontSize: TYPOGRAPHY.ONBOARDING.STEP_DESCRIPTION, 
              color: ACCENT_GRAY, 
              margin: '0 0 24px 0',
              lineHeight: 1.5
            }}>
              We couldn't verify your identity. Please check your documents and try again.
            </p>
          </div>
        );

      case 'not_started':
        return (
          <div style={{ textAlign: 'left', padding: '32px 0' }}>
            <p style={{ 
              fontSize: TYPOGRAPHY.ONBOARDING.STEP_DESCRIPTION, 
              color: ACCENT_GRAY, 
              margin: '0',
              lineHeight: 1.5,
              maxWidth: 400
            }}>
              Before you can start selling on Vesta, we need to confirm your identity. This protects buyers and ensures marketplace compliance.
            </p>
          </div>
        );

      default:
        return (
          <div style={{ textAlign: 'left', padding: '32px 0' }}>
            <p style={{ 
              fontSize: TYPOGRAPHY.ONBOARDING.STEP_DESCRIPTION, 
              color: ACCENT_GRAY, 
              margin: '0',
              lineHeight: 1.5,
              maxWidth: 400
            }}>
              Before you can start selling on Vesta, we need to confirm your identity. This protects buyers and ensures marketplace compliance.
            </p>
          </div>
        );
    }
  };

  return (
    <div style={{ 
      minHeight: '80vh', 
      display: 'flex', 
      flexDirection: 'column', 
      justifyContent: 'space-between', 
      alignItems: 'center', 
      width: '100%' 
    }}>
      <div style={{ width: '100%', maxWidth: 600, margin: '0 auto', paddingTop: 32, position: 'relative' }}>
        {/* Main Content */}
        <div style={{ 
          background: '#fff', 
          borderRadius: 16, 
          padding: 32, 
          border: '1px solid #E9ECEF',
          boxShadow: '0 2px 8px #0001'
        }}>
          {/* Card Header */}
          <div style={{ marginBottom: 32, display: 'flex', alignItems: 'center', gap: 16 }}>
            <ShieldDone
              set="bold"
              size={48}
              primaryColor={COLORS.PRIMARY_GRADIENT_START}
            />
            <h2 style={{ 
              fontWeight: 700, 
              fontSize: TYPOGRAPHY.ONBOARDING.H2, 
              margin: 0, 
              color: '#222'
            }}>
              Identity Verification
            </h2>
          </div>
          
          {renderStatusContent()}
          
          {/* Navigation Buttons - moved inside the card */}
          <div style={{ 
            marginTop: 32, 
            display: 'flex', 
            justifyContent: 'flex-end', 
            alignItems: 'center',
            width: '100%',
            gap: 12 
          }}>
            {/* Start Verification Button - show for not_started, requires_input, and failed states */}
            {(verificationStatus === 'not_started' || verificationStatus === 'requires_input' || verificationStatus === 'failed') && !isLoading && (
              <>
                <button
                  type="button"
                  onClick={startVerification}
                  disabled={isLoading}
                  style={{
                    background: COLORS.PRIMARY_GRADIENT,
                    color: '#fff',
                    border: 'none',
                    borderRadius: 10,
                    padding: '14px 32px',
                    fontSize: TYPOGRAPHY.ONBOARDING.BUTTON_TEXT,
                    fontWeight: 600,
                    cursor: 'pointer',
                    minHeight: 44,
                    minWidth: 200,
                    transition: 'all 0.2s',
                    boxShadow: '0 2px 8px #0002',
                  }}
                >
                  {verificationStatus === 'failed' || verificationStatus === 'requires_input' ? 'Try Again' : 'Start Verification'}
                </button>
                
                {/* Skip Button - temporary workaround for API issues */}
                <button
                  type="button"
                  onClick={() => {
                    console.log('Skipping identity verification temporarily');
                    setVerificationStatus('verified');
                  }}
                  style={{
                    background: 'transparent',
                    color: ACCENT_GRAY,
                    border: `1px solid ${ACCENT_GRAY}`,
                    borderRadius: 10,
                    padding: '14px 24px',
                    fontSize: TYPOGRAPHY.ONBOARDING.BUTTON_TEXT,
                    fontWeight: 500,
                    cursor: 'pointer',
                    minHeight: 44,
                    transition: 'all 0.2s',
                  }}
                >
                  Skip for Now
                </button>
              </>
            )}
            
            {/* Return to Seller Hub Button - show for pending state */}
            {verificationStatus === 'pending' && (
              <button
                type="button"
                onClick={() => window.location.href = '/seller-hub'}
                style={{
                  background: COLORS.PRIMARY_GRADIENT,
                  color: '#fff',
                  border: 'none',
                  borderRadius: 10,
                  padding: '14px 32px',
                  fontSize: TYPOGRAPHY.ONBOARDING.BUTTON_TEXT,
                  fontWeight: 600,
                  cursor: 'pointer',
                  minHeight: 44,
                  minWidth: 200,
                  transition: 'all 0.2s',
                  boxShadow: '0 2px 8px #0002',
                  marginLeft: 'auto'
                }}
              >
                Return to Seller Hub
              </button>
            )}
            
            {/* Continue Button - only show when verification is complete */}
            {verificationStatus === 'verified' && (
              <button
                type="button"
                onClick={() => window.location.href = '/seller-hub'}
                style={{
                  background: COLORS.PRIMARY_GRADIENT,
                  color: '#fff',
                  border: 'none',
                  borderRadius: 10,
                  padding: '14px 32px',
                  fontSize: TYPOGRAPHY.ONBOARDING.BUTTON_TEXT,
                  fontWeight: 600,
                  cursor: 'pointer',
                  minHeight: 44,
                  minWidth: 200,
                  transition: 'all 0.2s',
                  boxShadow: '0 2px 8px #0002',
                }}
              >
                Go to Seller Hub Now
              </button>
            )}
            
            {/* Loading State - show spinner when verification is starting */}
            {isLoading && (
              <button
                type="button"
                disabled
                style={{
                  background: COLORS.PRIMARY_GRADIENT,
                  color: '#fff',
                  border: 'none',
                  borderRadius: 10,
                  padding: '14px 32px',
                  fontSize: TYPOGRAPHY.ONBOARDING.BUTTON_TEXT,
                  fontWeight: 600,
                  cursor: 'not-allowed',
                  minHeight: 44,
                  minWidth: 200,
                  transition: 'all 0.2s',
                  opacity: 0.7,
                }}
              >
                Starting Verification...
              </button>
            )}
            
            {/* Spacer for left-aligned back button when no right button */}
            {(!verificationStatus || verificationStatus === 'pending') && (
              <div style={{ flex: 1 }}></div>
            )}
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div style={{ 
            marginTop: 16, 
            padding: 16, 
            background: '#FEF2F2', 
            border: '1px solid #FECACA', 
            borderRadius: 8,
            textAlign: 'center'
          }}>
            <span style={{ color: ERROR_RED, fontSize: TYPOGRAPHY.ONBOARDING.STEP_DESCRIPTION }}>
              {error}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
